// PRIMEIRO de todos os imports, e por efeito e não por chamada: `import` é
// içado, então uma chamada aqui em cima correria depois destes módulos serem
// avaliados — e é justamente um deles que lê storage e derruba o boot numa
// origem opaca (o chat dentro do site em sandbox). Ver `storage-shim.ts`.
import "@/shared/storage-shim";
import { QueryClient, VueQueryPlugin } from "@tanstack/vue-query";
import { createApp } from "vue";
import { useSessionState } from "@/features/auth/api/session";
import { forgetCacheWhenIdentityChanges } from "./session-cache";
import "@/design/tokens.css";
import { retryUnlessTheAnswerIsFinal } from "@/shared/http/retry";
import { currentPanelLocale, i18n, setPanelLocale } from "@/shared/i18n";
import App from "./App.vue";
import { watchForNewDeploy } from "./deploy-watch";
import { router } from "./router";

/**
 * `<html lang>` before the first paint.
 *
 * The seed already resolved the boot locale — stored hint, then browser, then
 * English — but nothing has touched the document yet, and `index.html` ships
 * `lang="en"`. Re-applying it here is what makes a screen reader announce the
 * first screen in the right language instead of the second one. The full cascade
 * (person, then workspace) refines it inside the shell, as soon as it knows.
 */
setPanelLocale(currentPanelLocale());

/**
 * A vigília do deploy começa junto com o app, e não com a primeira tela.
 *
 * Só agenda: quem age é a guarda em `router.ts`, na próxima navegação. Ver
 * `deploy-watch.ts` para o porquê de não recarregar na hora.
 */
watchForNewDeploy();

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 5_000,
			refetchOnWindowFocus: false,
			retry: retryUnlessTheAnswerIsFinal,
		},
	},
});

// Sair, ou entrar como outra pessoa, esquece tudo o que a conta anterior
// carregou — ver `session-cache.ts`.
forgetCacheWhenIdentityChanges(queryClient, useSessionState().identity);

createApp(App)
	.use(router)
	// The i18n seed: the shared dictionary only — features bring their own.
	.use(i18n)
	.use(VueQueryPlugin, { queryClient })
	.mount("#app");
